import React, { useState } from 'react'
import { RunInputFile, RunInputValue, useLazyGetRunFileDownloadUrlQuery } from '../../../store/customWorkflow/customWorkflows'
import { Button } from '../../Button/Button'
import { triggerDownload } from '../download'

export interface RunInputSummaryProps {
  inputValues: RunInputValue[]
  files: RunInputFile[]
  accountId?: string
  workflowId?: string
  runId?: string
}

const formatValue = (value: RunInputValue['value']): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

interface DownloadableFileProps {
  accountId: string
  workflowId: string
  runId: string
  fileId: string
  label: string
  fileName?: string | null
}

const DownloadableFile: React.FC<DownloadableFileProps> = ({ accountId, workflowId, runId, fileId, label, fileName }) => {
  const [trigger] = useLazyGetRunFileDownloadUrlQuery()
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const onOpen = async () => {
    if (loading) {
      return
    }
    setLoading(true)
    setFailed(false)
    try {
      const result = await trigger({ accountId, workflowId, runId, fileId, asAttachment: true }).unwrap()
      triggerDownload(result.url, fileName)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }
  return (
    <div className='d-flex align-items-center zocks-gap-2 paragraph-small'>
      <span className='zocks-text-gray-600'>{label}</span>
      <span>{fileName || 'file'}</span>
      <Button variant='secondary' loading={loading} onClick={() => void onOpen()}>
        Open / download
      </Button>
      {failed && <span className='zocks-text-danger'>Download failed.</span>}
    </div>
  )
}

export const RunInputSummary: React.FC<RunInputSummaryProps> = ({ inputValues, files, accountId, workflowId, runId }) => {
  const canDownload = !!accountId && !!workflowId && !!runId
  const downloadable = canDownload ? files.filter((file) => !!file.relatedId) : []
  const rest = canDownload ? files.filter((file) => !file.relatedId) : files
  const namedFiles = rest.filter((file) => !!file.fileName)
  const unnamedCount = rest.length - namedFiles.length

  if (inputValues.length === 0 && files.length === 0) return null

  return (
    <div className='mb-3'>
      {inputValues.map((item) => (
        <div key={item.variable} className='d-flex zocks-gap-1 paragraph-small'>
          <span className='zocks-text-gray-600'>{item.label}</span>
          <span>{formatValue(item.value)}</span>
        </div>
      ))}
      {downloadable.map((file, index) => (
        <DownloadableFile
          key={`${file.variable}-${file.relatedId}-${index}`}
          accountId={accountId!}
          workflowId={workflowId!}
          runId={runId!}
          fileId={file.relatedId!}
          label={file.label}
          fileName={file.fileName}
        />
      ))}
      {namedFiles.map((file, index) => (
        <div key={`${file.variable}-${index}`} className='d-flex zocks-gap-1 paragraph-small'>
          <span className='zocks-text-gray-600'>{file.label}</span>
          <span>{file.fileName}</span>
        </div>
      ))}
      {unnamedCount > 0 && (
        <div className='paragraph-small zocks-text-gray-600'>
          {unnamedCount} file{unnamedCount === 1 ? '' : 's'}
        </div>
      )}
    </div>
  )
}
